import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';

// Print styles for brochure layout - all 6 sections on 1 page
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 0;
    }
    
    * {
      background: white !important;
      background-color: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    body, html {
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .min-h-screen {
      padding: 5mm !important;
      min-height: auto !important;
    }
    
    /* Hide non-essential elements */
    .no-print, header, footer, .animate-bounce {
      display: none !important;
    }
    
    /* Show print header */
    .print-header {
      display: block !important;
      text-align: center;
      padding: 5px 0 8px 0;
      border-bottom: 2px solid #d97706;
      margin-bottom: 8px;
    }
    
    .print-header h1 {
      font-size: 20px !important;
      color: #000 !important;
      margin: 0 0 2px 0 !important;
    }
    
    .print-header p {
      font-size: 10px !important;
      color: #666 !important;
      margin: 0 !important;
    }
    
    /* No page breaks - all on one page */
    .print-page-break {
      page-break-after: avoid !important;
      break-after: avoid !important;
      margin-bottom: 6px !important;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    /* Step card styling */
    .print-step-card {
      page-break-inside: avoid;
      break-inside: avoid;
      border: 1px solid #d97706 !important;
      border-radius: 5px !important;
      background: white !important;
      box-shadow: none !important;
      overflow: hidden !important;
    }
    
    /* Compact layout for all 6 on 1 page */
    .print-step-card > div {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
    }
    
    .print-step-card .lg\\:w-1\\/2 {
      width: 18% !important;
      padding: 4px !important;
    }
    
    .print-step-card .lg\\:w-1\\/2:last-child {
      width: 82% !important;
      padding: 4px 8px !important;
    }
    
    /* Image styling */
    .print-step-card img {
      max-width: 70px !important;
      max-height: 50px !important;
      height: auto !important;
      border-radius: 4px !important;
      object-fit: cover !important;
      border: 1px solid #d97706 !important;
      padding: 1px !important;
      background: white !important;
    }
    
    /* Hide glow effects and image container padding */
    .blur-3xl, .bg-amber-500\\/10, .relative.w-full.max-w-sm {
      display: contents !important;
    }
    
    .blur-3xl, .bg-amber-500\\/10 {
      display: none !important;
    }
    
    /* Typography */
    h1, h2, h3, h4 {
      color: #000 !important;
    }
    
    h2 {
      font-size: 12px !important;
      margin-bottom: 2px !important;
      line-height: 1.1 !important;
    }
    
    p, span, li {
      color: #374151 !important;
      font-size: 8px !important;
      line-height: 1.2 !important;
    }
    
    .text-lg {
      font-size: 8px !important;
    }
    
    .text-amber-400, .text-amber-500 {
      color: #d97706 !important;
    }
    
    /* Step badge */
    .w-14 {
      width: 22px !important;
      height: 22px !important;
      min-width: 22px !important;
    }
    
    .w-14 span {
      font-size: 12px !important;
    }
    
    .rounded-2xl {
      border-radius: 4px !important;
    }
    
    /* Details list */
    ul {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    ul li {
      margin-bottom: 1px !important;
    }
    
    .space-y-3 > * + * {
      margin-top: 1px !important;
    }
    
    /* Hide step label on print */
    .text-amber-400.text-sm {
      display: none !important;
    }
    
    /* Section padding */
    section {
      padding: 0 !important;
      background: white !important;
    }
    
    .pb-16 {
      padding-bottom: 0 !important;
    }
    
    .mb-12 {
      margin-bottom: 6px !important;
    }
    
    .mb-6 {
      margin-bottom: 2px !important;
    }
    
    .gap-4 {
      gap: 3px !important;
    }
    
    .p-6, .md\\:p-10, .md\\:p-8 {
      padding: 4px !important;
    }
    
    /* Bullet points */
    .w-5.h-5 {
      width: 9px !important;
      height: 9px !important;
    }
    
    .w-2.h-2 {
      width: 3px !important;
      height: 3px !important;
    }
    
    /* Flex gap */
    .flex.items-center.gap-4 {
      gap: 4px !important;
    }
    
    .flex.items-start.gap-3 {
      gap: 3px !important;
    }
  }
`;

const HowItWorks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const steps = [
    {
      number: 1,
      title: "Receive Your Card",
      description: "Your premium Piper Card arrives at your doorstep. Each card comes with a unique QR code printed on it that links to your digital profile.",
      details: [
        "Premium black & gold NFC-enabled card",
        "Unique QR code printed on each card",
        "Works with all smartphones worldwide",
        "No batteries or charging required"
      ],
      image: "https://customer-assets.emergentagent.com/job_pipercard-shop/artifacts/nwgsh7gn_image.png",
      imageAlt: "Piper Card NFC Business Card"
    },
    {
      number: 2,
      title: "Enable NFC on Your Phone",
      description: "Make sure NFC is enabled on your smartphone to tap and read NFC cards. iPhone users have NFC always on, Android users need to enable it in settings.",
      details: [
        "iPhone: NFC is always ON (iPhone 7+)",
        "Android: Settings → Connected devices → NFC → ON",
        "Or swipe down and tap NFC in Quick Settings",
        "Remove phone case if card not reading"
      ],
      image: "https://static.prod-images.emergentagent.com/jobs/71373191-2287-4890-8e55-cb08e918f304/images/ba9b00070623f20326207ced71c4d19a0cb113e161336b91fd8048bd854d578f.png",
      imageAlt: "Enable NFC settings on phone",
      isNFCStep: true
    },
    {
      number: 3,
      title: "Scan the QR Code or Tap Your Phone",
      description: "Use your smartphone camera to scan the QR code printed on the card, or simply tap the card on the back of your phone to activate NFC.",
      details: [
        "Open your phone's camera app",
        "Point at the QR code on the card",
        "Or hold the card near your phone's NFC area",
        "You'll be redirected to Piper Card website"
      ],
      image: "https://static.prod-images.emergentagent.com/jobs/f32caa83-4b72-44e0-b14a-76791fd0a0d8/images/7a3791832839993441669fc488d02a43e41758e73d93f5c0a029093b5233d525.png",
      imageAlt: "Scanning QR code or tapping NFC card"
    },
    {
      number: 4,
      title: "Sign In to Your Account",
      description: "Log into your Piper Card account using Google, Email OTP, or Phone number. If you're new, creating an account takes just seconds.",
      details: [
        "Sign in with Google (one click)",
        "Or use Email OTP (passwordless)",
        "Or sign in with Phone number",
        "New users can create account instantly"
      ],
      image: "https://customer-assets.emergentagent.com/job_biz-card-pro-1/artifacts/vx8uuyj6_image.png",
      imageAlt: "Sign in screen with multiple options"
    },
    {
      number: 5,
      title: "Link Your Card or Create New",
      description: "Connect the NFC card to your existing digital business card, or create a brand new one. Fill in your professional details, add photo, and social links.",
      details: [
        "Link to existing card if you have one",
        "Or create a new digital card",
        "Add your name, title, company",
        "Upload profile photo & company logo",
        "Add email, phone, social media links"
      ],
      image: "https://customer-assets.emergentagent.com/job_biz-card-pro-1/artifacts/il4iuroo_image.png",
      imageAlt: "Create card form interface"
    },
    {
      number: 6,
      title: "Start Using Your Piper Card",
      description: "You're all set! Tap your card on any smartphone to instantly share your digital business card. Recipients can save your contact with one click.",
      details: [
        "Tap card on any smartphone to share",
        "Works on iPhone & Android",
        "No app needed for recipients",
        "One-click save to contacts",
        "Share via QR code or link too"
      ],
      image: "https://customer-assets.emergentagent.com/job_biz-card-pro-1/artifacts/ult2iv9d_image.png",
      imageAlt: "Digital business card profile"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />

      {/* Print-only Header for Brochure */}
      <div className="print-header hidden">
        <h1>How to Use Your Piper Card</h1>
        <p>6 Simple Steps to Activate Your NFC Business Card</p>
      </div>

      {/* Hero Section - Hidden on Print */}
      <section className="py-12 md:py-16 px-4 text-center no-print">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
          How to Use Your <span className="text-amber-400">Piper Card</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-6">
          Follow these 6 simple steps to activate your NFC card and start sharing your digital business card instantly
        </p>
        
        <ChevronDown className="w-8 h-8 text-amber-500 mx-auto animate-bounce" />
      </section>

      {/* Steps Section */}
      <section className="pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.number} className="mb-12 print-page-break" data-testid={`step-${step.number}`}>
              {/* Step Card */}
              <div className="bg-gray-800/60 backdrop-blur-sm rounded-3xl overflow-hidden border border-gray-700/50 print-step-card">
                <div className={`lg:flex ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  
                  {/* Image Side */}
                  <div className="lg:w-1/2 p-6 md:p-8 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <div className="relative w-full max-w-sm">
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-amber-500/10 blur-3xl rounded-full no-print"></div>
                      <img 
                        src={step.image}
                        alt={step.imageAlt}
                        className={`relative w-full h-auto shadow-2xl ${step.number === 1 ? 'rounded-2xl' : 'rounded-3xl'}`}
                      />
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="lg:w-1/2 p-6 md:p-10 flex flex-col justify-center">
                    {/* Step Badge */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                        <span className="text-white font-bold text-2xl">{step.number}</span>
                      </div>
                      <div>
                        <span className="text-amber-400 text-sm font-medium">Step {step.number} of 6</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-white">{step.title}</h2>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Details List */}
                    <ul className="space-y-3">
                      {step.details.map((detail, dIndex) => (
                        <li key={dIndex} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                          </div>
                          <span className="text-gray-400">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Connector Arrow */}
              {index < steps.length - 1 && (
                <div className="flex justify-center my-6 no-print">
                  <div className="w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
                    <ChevronDown className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Quick Summary Section */}
      <section className="py-12 px-4 bg-gray-800/30 border-y border-gray-700/50 no-print">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-white text-center mb-8">Quick Summary</h3>
          <div className="grid grid-cols-6 gap-2 md:gap-4">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-2 text-white font-bold text-sm md:text-base">
                  {step.number}
                </div>
                <p className="text-gray-400 text-xs md:text-sm hidden md:block">{step.title.split(' ').slice(0, 2).join(' ')}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 no-print">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Get Your Card?
              </h2>
              <p className="text-amber-100 text-lg mb-8 max-w-lg mx-auto">
                Order your NFC business card now and start networking smarter
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/shop')}
                  size="lg"
                  className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-lg"
                  data-testid="cta-get-started-button"
                >
                  Get Your Business Card
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
                  data-testid="cta-order-card-button"
                >
                  Order NFC Card
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
